import { useMemo } from 'react';

import { useGetDatasetsInfiniteQuery } from 'src/apiClients';
import type { PagedQueryArg } from 'src/apiClients/queries';
import { DEFAULT_PAGE_SIZE } from 'src/apiClients/queries';

import { LazyCountChip } from './LazyCountChip';

export interface WorkspaceDatasetCountChipProps {
    workspaceId: string;
    tenantId: string;
}

/**
 * Renders the dataset count for a workspace row. Defers the query until the
 * element scrolls into view to avoid an N+1 request burst when many workspace
 * rows are rendered simultaneously. Shares its RTK Query cache entry with the
 * expansion's dataset query so opening a workspace becomes a cache hit.
 */
export const WorkspaceDatasetCountChip = ({
    workspaceId,
    tenantId,
}: WorkspaceDatasetCountChipProps) => {
    const queryArg = useMemo<PagedQueryArg>(
        () => ({
            limit: DEFAULT_PAGE_SIZE,
            order: [
                ['state', 'ASC'],
                ['name', 'ASC'],
            ],
            where: { workspaceId },
            extraParams: { tenantId },
        }),
        [workspaceId, tenantId],
    );

    const useCount = (enabled: boolean) => {
        const { data, isLoading, isError } = useGetDatasetsInfiniteQuery(queryArg, {
            skip: !enabled,
        });
        return {
            count: data ? (data.pages[0]?.total ?? 0) : undefined,
            isLoading,
            isError,
        };
    };

    return <LazyCountChip useCount={useCount} />;
};
